import { Case, CaseInspection, CaseStatus } from "@/api/rest/models/warranty";

export const COMPLETED_STATUSES = [CaseStatus.SOLVED, CaseStatus.CLOSED];

export interface InspectionGroup {
  inspection: CaseInspection;
  householdId: number;
  cases: Case[];
  completedCount: number;
}

// Defects keep the inspection act's order (Hausing creates them in sequence), so the list matches the paper act.
const byActOrder = (a: Case, b: Case) => a.createdAt.localeCompare(b.createdAt) || a.id - b.id;

export const isCompleted = (c: Case) => COMPLETED_STATUSES.includes(c.status);

export const inspectionDefects = (cases: Case[], inspectionId: number): Case[] =>
  cases.filter((c) => c.inspection?.id === inspectionId).sort(byActOrder);

/**
 * Splits cases into standalone cases and inspection groups.
 *
 *   [defect A (insp 1), case X, defect B (insp 1)]  →  standalone: [X], groups: [insp 1: A, B]
 */
export const groupByInspection = (cases: Case[]): { standalone: Case[]; groups: InspectionGroup[] } => {
  const standalone: Case[] = [];
  const groups = new Map<number, InspectionGroup>();

  for (const c of cases) {
    if (!c.inspection) {
      standalone.push(c);
      continue;
    }
    const group = groups.get(c.inspection.id) ?? {
      inspection: c.inspection,
      householdId: c.householdId,
      cases: [],
      completedCount: 0,
    };
    group.cases.push(c);
    if (isCompleted(c)) {
      group.completedCount++;
    }
    groups.set(c.inspection.id, group);
  }

  for (const group of groups.values()) {
    group.cases.sort(byActOrder);
  }
  return { standalone, groups: [...groups.values()] };
};
